import { throttle } from '../../config';
import { deepCopy } from '../../utilities/deep-copy';
import { wait } from '../../utilities/wait';
import { shippingPolicy } from './data';

const API_URL = '/api/shipping';
const ACCOUNT_DOMAIN = 'AUCTIONHOUSE';
class ShippingApi {
  /* eslint-disable require-jsdoc */
  async postShippingReview(catalogID) {
  try {
    await fetch(
      `/cps/app/catalog/${catalogID}/shipping-review`,
      {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: null
      }
    );
  } catch (err) {
    console.error('Error creating shipping review for lots => ', err);
  }
  }

  async getBulkShipmentDetails(uniqueShipments, houseID) {
    const bodyData = { shipments: Array.from(uniqueShipments.keys()).map(key => {
      return { id: uniqueShipments.get(key).shipmentID, topic: `memberref:${uniqueShipments.get(key).memberRef}` };
    }) };

    const options = {
      method: 'PATCH',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-invaluable-shipping-account-id': houseID,
        'x-invaluable-shipping-account-domain': 'AUCTIONHOUSE',
      },
      body: JSON.stringify(bodyData),
    };
    try {
      const response = await fetch(`${API_URL}/shipments/bulk-get`, options);
      const data = await response.json();
      return data;
    } catch (err) {
      console.log('Error fetching bulk shipment details -----', err);
    }
  }

  async getShippingPolicy(accountId, accountDomain) {
    const options = {
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Accept': 'application/json',
        'x-invaluable-shipping-account-id': accountId,
        'x-invaluable-shipping-account-domain': accountDomain,
      },
    };
    try {
      const response = await fetch(`${API_URL}/policy`, options);
      const data = await response.json();
      return data;
    } catch (error) {
      console.log('get shipping policy -----', error);
      return error;
    }
  }

  // Fetches the short-lived, HMAC-signed Tipalti payee-dashboard URL from the
  // shipping-svc signing endpoint added in COR-3075 (the back end for this page).
  // Requires an authenticated principal (owning house / super admin), so send
  // credentials — the proxied /api/shipping path is same-origin and the LiveUser
  // cookie flows with the request.
  //
  // Returns { status, data } rather than the bare envelope the older methods here
  // return, and is the shape to prefer for new calls: the caller needs the status
  // to choose its copy and to decide whether a Retry could ever succeed. 404 (the
  // house has no payee record yet) and 401/403 are permanent; 502 (Tipalti down or
  // unconfigured) and network failures are not. `data` is the unwrapped
  // { iframeUrl, expiresAt }; `status` is 0 when no response came back at all.
  async getTipaltiIframeAuth(accountId, signal) {
    const options = {
      method: 'GET',
      credentials: 'include',
      signal,
      headers: {
        'Accept': 'application/json',
        'x-invaluable-shipping-account-id': accountId,
        'x-invaluable-shipping-account-domain': ACCOUNT_DOMAIN,
      },
    };
    try {
      const response = await fetch(`${API_URL}/tipalti/iframe-auth`, options);
      if (!response.ok) {
        return { status: response.status, data: null };
      }
      const body = await response.json();
      return { status: response.status, data: (body && body.data) || null };
    } catch (error) {
      // An abort is the caller unmounting, not a failure worth logging.
      if (error.name !== 'AbortError') {
        console.error('Error fetching Tipalti iframe auth:', error);
      }
      return { status: 0, data: null };
    }
  }

  async getShippingOriginById(accountId, accountDomain, originId) {
    const options = {
      method: 'GET',
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Accept': 'application/json',
        'x-invaluable-shipping-account-id': accountId,
        'x-invaluable-shipping-account-domain': accountDomain,
      },
    };
    try {
      const response = await fetch(`${API_URL}/origin/${originId}`, options);
      const data = await response.json();
      return data;
    } catch (error) {
      console.log('get shipping policy -----', error);
      return error;
    }
  }

  async saveShippingLocation(formData, accountId, accountDomain) {
    const options = {
      method: 'POST',
      body: JSON.stringify(formData),
      headers: {
        'Access-Control-Allow-Origin': '*',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-invaluable-shipping-account-id': accountId,
        'x-invaluable-shipping-account-domain': accountDomain,
      },
    };
    try {
      const response = await fetch(`${API_URL}/origin`, options);
      const data = await response.json();
      return data;
    } catch (error) {
      console.log('submit shipping location errors -----', error);
      return error;
    }
  }

  async fetchReviews(catalogRef, houseID) {
    const options = {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'x-invaluable-shipping-account-domain': 'AUCTIONHOUSE',
        'x-invaluable-shipping-account-id': houseID
      },
    }
    try {
      const response = await fetch(`${API_URL}/reviews?topic=catalogref:${catalogRef}`, options);
      const data = await response.json();
      return data;
    } catch (err) {
      console.log('Error fetching shipping reviews --------- ', err);
      return err;
    }
  }

  async fetchReviewById(reviewId, houseId) {
    const options = {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'x-invaluable-shipping-account-domain': 'AUCTIONHOUSE',
        'x-invaluable-shipping-account-id': houseId,
      }
    };
    try{
      const response = await fetch(`${API_URL}/review/${reviewId}`, options);
      const data = await response.json();
      return data;
    } catch (err) {
      console.log('Error fetching shipping review by ID:', reviewId, err);
      return err;
      }
  }

  async putShippingReview(reviewID, houseID, payload) {
    const options = {
      method: 'PUT',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'x-invaluable-shipping-account-domain': 'AUCTIONHOUSE',
        'x-invaluable-shipping-account-id': houseID
      },
      body: JSON.stringify(payload)
    }
    try {
      const response = await fetch(`${API_URL}/review/${reviewID}`, options);
      const data = await response.json();
      return data;
    } catch (err) {
      console.log('Error updating shipping review data --------- ', err);
      return err;
    }
  }

  async fetchArtaSubtypes(signal) {
    const options = {
      signal,
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    };
    try {
      const response = await fetch(`${API_URL}/arta/metadata?types=objects`, options);
      const data = await response.json();
      return data.data.objects;
    } catch (error) {
      console.log('Error fetching data from Arta API:', error);
    }
  };

  // Static data calls
  async getShippingTerms(request = {}) {
    if (throttle) {
      await wait(throttle);
    }

    const data = deepCopy(shippingPolicy);

    const count = data.length;

    return Promise.resolve({
      data,
      count
    });
  }
}

export const shippingApi = new ShippingApi();
